import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/shared/Logo'
import { portalApi } from '@/api/portal'
import { NotificationBell } from '@/components/shared/NotificationBell'
import { TermsGateModal } from '@/components/shared/TermsGateModal'
import { PreviewBlockedDialog } from '@/components/shared/PreviewBlockedDialog'
import { usePortalPermissions } from '@/hooks/usePortalPermissions'
import { buildBrandStyle, isHeaderLight, useBrandFont } from '@/lib/brandTheme'
import { LayoutDashboard, Users, ShieldCheck, CalendarDays, Receipt, LogOut, Eye, X, Building2, Menu, CalendarPlus } from 'lucide-react'

export default function PortalLayout() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const previewClientName = sessionStorage.getItem('previewClientName')
  const permissions = usePortalPermissions()
  const [mobileOpen, setMobileOpen] = useState(false)

  const { data: branding } = useQuery({
    queryKey: ['portal-branding'],
    queryFn: () => portalApi.getBranding().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const { data: termsStatus } = useQuery({
    queryKey: ['portal-terms-status'],
    queryFn: () => portalApi.getTermsStatus().then(r => r.data),
    staleTime: 60 * 1000,
  })

  useBrandFont(branding?.fontFamily)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const hasPrimary = !!branding?.primaryColour
  const lightHeader = branding ? isHeaderLight(branding) : false
  const brandVars = branding ? buildBrandStyle(branding) : undefined

  const headerStyle = hasPrimary && !lightHeader
    ? { backgroundColor: branding!.primaryColour! }
    : undefined

  const headerClass = lightHeader
    ? 'bg-white border-b border-gray-200'
    : hasPrimary
      ? 'shadow-sm'
      : 'bg-gradient-to-r from-indigo-600 to-indigo-700 shadow-sm'

  // Nav link colours adapt to header style: solid dark bar vs pale/white bar
  const textBase   = lightHeader ? 'text-gray-500'   : 'text-white/70'
  const textHover  = lightHeader ? 'hover:text-gray-900' : 'hover:text-white'
  const textActive = lightHeader ? 'text-gray-900'   : 'text-white'
  const activeBg   = lightHeader ? 'bg-[var(--brand-secondary)]/10' : 'bg-white/15'
  const hoverBg    = lightHeader ? 'hover:bg-gray-100' : 'hover:bg-white/8'

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-[var(--brand-radius-sm)] transition-all ${
      isActive
        ? `${activeBg} ${textActive}`
        : `${textBase} ${hoverBg} ${textHover}`
    }`

  const btnClass = `flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-[var(--brand-radius-sm)] transition-all cursor-pointer ml-2 ${textBase} ${hoverBg} ${textHover}`

  const handleClosePreview = () => {
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('previewClientName')
    window.close()
  }

  // Mobile dropdown menu items always show their label (unlike the desktop nav's icon-only
  // row at this width) and use fixed neutral colours regardless of the tenant's brand header,
  // so the menu stays readable no matter what colour the header itself is.
  const mobileNavClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 text-sm font-medium px-4 py-3 rounded-lg transition-colors ${
      isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
    }`

  return (
    <div className="min-h-screen bg-slate-50" style={brandVars}>
      <PreviewBlockedDialog />
      {/* Preview mode banner */}
      {previewClientName && (
        <div className="print:hidden bg-amber-400 text-amber-900 text-sm font-medium px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye size={15} />
            Previewing as <strong className="font-semibold">{previewClientName}</strong> — read-only view of what the client manager sees
          </div>
          <button
            onClick={handleClosePreview}
            className="flex items-center gap-1 hover:text-amber-950 cursor-pointer transition-colors"
          >
            <X size={14} /> Close preview
          </button>
        </div>
      )}

      <header className={`print:hidden ${headerClass}`} style={headerStyle}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 gap-2">

          {/* Branding */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {branding?.logoUrl && (
              <img
                src={branding.logoUrl}
                alt={branding.displayName ?? 'Company logo'}
                className="h-11 max-w-44 object-contain object-left shrink-0"
              />
            )}
            {!branding?.logoUrl && !branding?.displayName && (
              <Logo size={36} theme={lightHeader ? 'light' : 'dark'} />
            )}
            {branding?.displayName && (
              <span
                className="font-bold text-lg leading-tight truncate"
                style={{ color: lightHeader ? '#111111' : 'var(--brand-primary-contrast)' }}
              >
                {branding.displayName}
              </span>
            )}
          </div>

          {/* Nav — full row on tablet/desktop */}
          <nav className="hidden sm:flex items-center gap-0.5 shrink-0">
            <NavLink to="/portal/overview" className={navClass}>
              <LayoutDashboard size={15} />
              <span>Overview</span>
            </NavLink>
            <NavLink to="/portal/team" className={navClass}>
              <Users size={15} />
              <span>My Team</span>
            </NavLink>
            <NavLink to="/portal/compliance" className={navClass}>
              <ShieldCheck size={15} />
              <span>Compliance</span>
            </NavLink>
            <NavLink to="/portal/bookings" className={navClass}>
              <CalendarDays size={15} />
              <span>Bookings</span>
            </NavLink>
            {permissions.bookSessions && (
              <NavLink to="/portal/sessions" className={navClass}>
                <CalendarPlus size={15} />
                <span>Open sessions</span>
              </NavLink>
            )}
            {permissions.invoices && (
              <NavLink to="/portal/invoices" className={navClass}>
                <Receipt size={15} />
                <span>Invoices</span>
              </NavLink>
            )}
            <NavLink to="/portal/profile" className={navClass}>
              <Building2 size={15} />
              <span>Our details</span>
            </NavLink>
            <NotificationBell
              api={portalApi}
              buttonClass={`flex items-center justify-center w-9 h-9 rounded-[var(--brand-radius-sm)] transition-colors cursor-pointer ${textBase} ${hoverBg} ${textHover}`}
            />
            <button onClick={handleLogout} className={btnClass}>
              <LogOut size={15} />
              <span>Sign out</span>
            </button>
          </nav>

          {/* Mobile: notification bell + hamburger toggle */}
          <div className="flex sm:hidden items-center gap-1 shrink-0">
            <NotificationBell
              api={portalApi}
              buttonClass={`flex items-center justify-center w-9 h-9 rounded-[var(--brand-radius-sm)] transition-colors cursor-pointer ${textBase} ${hoverBg} ${textHover}`}
            />
            <button
              onClick={() => setMobileOpen(v => !v)}
              className={`flex items-center justify-center w-9 h-9 rounded-[var(--brand-radius-sm)] transition-colors cursor-pointer ${textBase} ${hoverBg} ${textHover}`}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileOpen && (
          <nav className="sm:hidden bg-white border-t border-gray-200 px-3 py-2 flex flex-col gap-0.5">
            <NavLink to="/portal/overview" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
              <LayoutDashboard size={17} /> Overview
            </NavLink>
            <NavLink to="/portal/team" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
              <Users size={17} /> My Team
            </NavLink>
            <NavLink to="/portal/compliance" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
              <ShieldCheck size={17} /> Compliance
            </NavLink>
            <NavLink to="/portal/bookings" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
              <CalendarDays size={17} /> Bookings
            </NavLink>
            {permissions.bookSessions && (
              <NavLink to="/portal/sessions" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
                <CalendarPlus size={17} /> Open sessions
              </NavLink>
            )}
            {permissions.invoices && (
              <NavLink to="/portal/invoices" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
                <Receipt size={17} /> Invoices
              </NavLink>
            )}
            <NavLink to="/portal/profile" className={mobileNavClass} onClick={() => setMobileOpen(false)}>
              <Building2 size={17} /> Our details
            </NavLink>
            <button
              onClick={() => { setMobileOpen(false); handleLogout() }}
              className="flex items-center gap-3 text-sm font-medium px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 cursor-pointer text-left"
            >
              <LogOut size={17} /> Sign out
            </button>
          </nav>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Outlet />
      </main>

      {termsStatus?.termsRequired && (
        <TermsGateModal status={termsStatus} companyName={branding?.displayName} />
      )}
    </div>
  )
}
